import { IPromise, IQService, IRootScopeService, IScope } from "angular";
import { SCIMService } from "@gtmhub/configuration/services/scim.service";
import { IRestLayerRequest, createRestLayerFields } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { IIndicator, IIndicatorProgress, UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { IRole, RoleService } from "@gtmhub/roles";
import {
  GridFilterDefinitions,
  GridSortingDefinitions,
  IArrayGridFilter,
  IGridFiltersChange,
  IGridSortingRule,
  OrFiltersGroup,
  RestLayerFilterFactory,
  addValueToExistingFiltersOrCreateNewFilterInEveryGroup,
  createFilter,
  getCommonValuesUsedInAllFiltersInEveryGroup,
  getPersistedScreenSettings,
  removeValueFromFiltersInEveryGroup,
  setPersistedScreenSettings,
  toGridDefinitionMap,
  toRestLayerRequest,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { USER_ROLES_VIEWONLY } from "@gtmhub/shared/utils/user-roles";
import { UserInvitationModalService } from "@gtmhub/users/components/user-invitation-form/user-invitation-modal.service";
import { toIdMap } from "@gtmhub/util";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { AccountService } from "../../accounts/accounts-service";
import { IUser } from "../models";
import { UserService } from "../user-service";
import { generateDynamicTeamIdsFilter } from "./users-filters.util";

type ICustomUser = Pick<IUser, "id" | "firstName" | "lastName" | "picture" | "subscriptionType" | "roles" | "email" | "isActive">;

interface IUsersRootScopeService extends IRootScopeService {
  noNavigation: boolean;
}

export interface IUserListScope extends IScope {
  filterDefinitions: GridFilterDefinitions;
  filters: OrFiltersGroup[];
  sortingDefinitions: GridSortingDefinitions;
  sortingRules: IGridSortingRule[];
  filtersOrSortingChange(change?: IGridFiltersChange): void;
  subscriptionTypesCount: number;
  indicators: {
    loading?: IIndicator;
    applyingRoles?: IIndicator;
    gettingUsers?: IIndicator;
    activating?: IIndicator;
    deactivating?: IIndicator;
    deleting?: IIndicator;
  };
  roles: Record<string, IRole>;
  users: ICustomUser[];
  multiSelect: boolean;
  multiselection: string[];
  activateUsersIds: string[];
  deactivateUsersIds: string[];
  roleMultiselection: string[];
  showRolesSwitcher: boolean;
  showActivateDialog: boolean;
  showMaximumLimitReached: boolean;
  showDeactivateDialog: boolean;
  showDeleteDialog: boolean;
  filterRoleSelection: Set<string>;
  currentPage: number;
  totalPages: number;
  shouldEnableRoleUpdates: boolean;
  onPageChange(page: number): void;
  toggleUserInMultiselection(id: string): void;
  toggleAll(): void;
  toggleRoleInMultiselection(roleId: string): void;
  toggleRoleFilter(id: string): void;
  toggleBulkSelect(): void;
  cancelBulkSelect(): void;
  toggleRoleSwitcher(): void;
  toggleMaximumLimitReached(): void;
  applyRoles(): void;
  loadUsers(): void;
  activateUsers(): void;
  deactivateUsers(): void;
  deleteUsers(): void;
  openInvitationModal(): void;
  displayActivateDialog(): void;
  displayDeactivateDialog(): void;
  hideActivateDialog(): void;
  hideDeactivateDialog(): void;
  contactUs(): void;
}

const usersPerPage = 30;

const roleFieldName = "roles._id";

const createRoleFilter = (roleIds: string[]): IArrayGridFilter => ({
  dataType: "array",
  fieldName: roleFieldName,
  operator: "isOneOf",
  value: roleIds,
});

const screenPath = ["users"];

export class UsersListCtrl implements IStateInit {
  private userMap: { [key: string]: ICustomUser };
  private viewOnlyRole?: IRole;

  public static $inject = [
    "$rootScope",
    "$scope",
    "UserService",
    "RoleService",
    "$q",
    "UIErrorHandlingService",
    "AccountService",
    "UserInvitationModalService",
    "AnalyticsService",
    "SCIMService",
  ];

  constructor(
    private $rootScope: IUsersRootScopeService,
    private $scope: IUserListScope,
    private userService: UserService,
    private roleService: RoleService,
    private $q: IQService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private accountService: AccountService,
    private userInvitationModal: UserInvitationModalService,
    private analyticsService: AnalyticsService,
    private scimService: SCIMService
  ) {
    this.$scope.indicators = {
      loading: { progress: true },
      gettingUsers: { progress: true },
    };
    this.$rootScope.noNavigation = false;
    this.$scope.toggleUserInMultiselection = this.toggleUserInMultiselection;
    this.$scope.currentPage = 1;
    this.$scope.onPageChange = this.onPageChange;
    this.$scope.applyRoles = this.applyRoles;
    this.$scope.activateUsers = this.activateUsers;
    this.$scope.deactivateUsers = this.deactivateUsers;
    this.$scope.deleteUsers = this.deleteUsers;
    this.$scope.toggleAll = this.toggleAll;
    this.$scope.loadUsers = this.loadUsers;
    this.$scope.toggleRoleInMultiselection = this.toggleRoleInMultiselection;
    this.$scope.toggleRoleFilter = this.toggleRoleFilter;
    this.$scope.toggleBulkSelect = this.toggleBulkSelect;
    this.$scope.cancelBulkSelect = this.cancelBulkSelect;
    this.$scope.toggleRoleSwitcher = this.toggleRoleSwitcher;
    this.$scope.toggleMaximumLimitReached = this.toggleMaximumLimitReached;
    this.$scope.openInvitationModal = this.openInvitationModal;
    this.$scope.displayActivateDialog = this.displayActivateDialog;
    this.$scope.displayDeactivateDialog = this.displayDeactivateDialog;
    this.$scope.hideActivateDialog = this.hideActivateDialog;
    this.$scope.hideDeactivateDialog = this.hideDeactivateDialog;
    this.$scope.contactUs = this.contactUs;

    this.$scope.$on("usersChanged", this.usersChanged);
    this.$scope.$on("userDeleted", this.usersChanged);
    this.$scope.$on("userSubscriptionUpdated", this.userChanged);

    this.$scope.roleMultiselection = [];
    this.$scope.filterRoleSelection = new Set();
  }

  public stateInit(): IPromise<unknown> {
    this.initGridFilteringBar();
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.clearMultiselection();

    return this.$q
      .all({
        roles: this.roleService.getRoles(),
        accountSubscription: this.accountService.getAccountSubscription(),
        isScimProvisioningOn: this.scimService.checkIfScimProvisioningIsOn(),
      })
      .then(
        ({ roles, accountSubscription, isScimProvisioningOn }) => {
          this.viewOnlyRole = roles.items.find((role) => role.name === USER_ROLES_VIEWONLY);
          this.$scope.roles = toIdMap(roles.items);
          this.$scope.shouldEnableRoleUpdates = !isScimProvisioningOn;

          this.$scope.subscriptionTypesCount = accountSubscription.subscriptionTypesCount;
          delete this.$scope.indicators.loading;
        },
        (error) => (this.$scope.indicators.loading = { error })
      );
  };

  private openInvitationModal = () => {
    this.userInvitationModal.open();
  };

  private displayActivateDialog = () => {
    this.userService
      .getUserLimit(this.$scope.multiselection.length)
      .then(({ isLimitReached }) => {
        if (isLimitReached) {
          this.toggleMaximumLimitReached();
        } else {
          this.$scope.showActivateDialog = !this.$scope.showActivateDialog;
        }
      })
      .catch((err) => this.uiErrorHandlingService.handleModal(err));
  };

  private hideActivateDialog = () => {
    this.$scope.showActivateDialog = false;
  };

  private hideDeactivateDialog = () => {
    this.$scope.showDeactivateDialog = false;
  };

  private displayDeactivateDialog = () => {
    this.$scope.showDeactivateDialog = !this.$scope.showDeactivateDialog;
  };

  private contactUs = () => {
    this.analyticsService.contactSales("contactSales");
  };

  private initGridFilteringBar = () => {
    this.$scope.filterDefinitions = toGridDefinitionMap([
      {
        dataType: "text",
        fieldName: "fullName",
        displayName: localize("name_cap"),
        trackingName: "Name",
        defaultOperator: "equals",
        supportedOperators: ["equals", "notEqual", "contains", "doesNotContain"],
      },
      {
        dataType: "text",
        fieldName: "email",
        displayName: localize("email"),
        trackingName: "Email",
        defaultOperator: "equals",
        supportedOperators: ["equals", "notEqual", "contains", "doesNotContain"],
      },
      {
        dataType: "array",
        arrayType: "team",
        fieldName: "teamIds",
        displayName: localize("teams_cap"),
        trackingName: "Teams",
        defaultOperator: "contains",
        supportedOperators: ["contains"],
      },
      {
        dataType: "array",
        arrayType: "role",
        fieldName: roleFieldName,
        displayName: localize("role"),
        trackingName: "Role",
        defaultOperator: "isOneOf",
        supportedOperators: ["isOneOf"],
      },
    ]);
    let persistedFilters = getPersistedScreenSettings(screenPath, "filters");
    // TODO delete this after few months of completing GVS-20612
    if (persistedFilters) {
      persistedFilters = persistedFilters.map(
        (filtersGroup) => (filtersGroup = filtersGroup.map((filter) => (filter.fieldName === "name" ? { ...filter, fieldName: "fullName" } : filter)))
      );
    }
    this.$scope.filters = persistedFilters || [
      [
        createFilter(this.$scope.filterDefinitions["fullName"]),
        createFilter(this.$scope.filterDefinitions["email"]),
        createFilter(this.$scope.filterDefinitions["teamIds"]),
        createRoleFilter([]),
      ],
    ];

    this.$scope.sortingDefinitions = toGridDefinitionMap([
      {
        fieldName: "firstName",
        displayName: localize("first_name_cap"),
        trackingName: "First name",
      },
    ]);
    this.$scope.sortingRules = getPersistedScreenSettings(screenPath, "sorting") || [{ fieldName: "firstName", reversed: false }];

    this.$scope.filtersOrSortingChange = this.filtersOrSortingChange;
  };

  private filtersOrSortingChange = (change?: IGridFiltersChange) => {
    if (!change || !change.initial) {
      setPersistedScreenSettings(screenPath, "filters", this.$scope.filters);
      setPersistedScreenSettings(screenPath, "sorting", this.$scope.sortingRules);
    }

    this.$scope.filterRoleSelection = getCommonValuesUsedInAllFiltersInEveryGroup(this.$scope.filters, { fieldName: roleFieldName });

    this.$scope.currentPage = 1;
    this.loadUsers();
  };

  private toggleRoleFilter = (roleId: string) => {
    const args = { fieldName: roleFieldName, value: roleId, createFilter: createRoleFilter };
    this.$scope.filters = this.$scope.filterRoleSelection.has(roleId)
      ? removeValueFromFiltersInEveryGroup(this.$scope.filters, args)
      : addValueToExistingFiltersOrCreateNewFilterInEveryGroup(this.$scope.filters, args);

    this.filtersOrSortingChange();
  };

  private toggleRoleInMultiselection = (roleId: string) => {
    const index = this.$scope.roleMultiselection.indexOf(roleId);

    if (index !== -1) {
      this.$scope.roleMultiselection.splice(index, 1);
    } else {
      const isSelectedRoleViewOnly = roleId === this.viewOnlyRole?.id;
      const hasViewOnlyBeenSelected = this.$scope.roleMultiselection.some((id) => id === this.viewOnlyRole?.id);

      if (isSelectedRoleViewOnly || hasViewOnlyBeenSelected) {
        this.$scope.roleMultiselection = [];
      }

      this.$scope.roleMultiselection.push(roleId);
    }
  };

  private toggleAll = () => {
    if (this.$scope.multiselection.indexOf(this.$scope.users[0].id) !== -1) {
      this.clearMultiselection();
    } else {
      for (let i = 0; i < this.$scope.users.length; i++) {
        this.$scope.multiselection.push(this.$scope.users[i].id);
        if (this.$scope.users[i].isActive) {
          this.$scope.deactivateUsersIds.push(this.$scope.users[i].id);
        } else {
          this.$scope.activateUsersIds.push(this.$scope.users[i].id);
        }
      }
    }
  };

  private toggleBulkSelect = () => {
    this.$scope.multiSelect = !this.$scope.multiSelect;
    this.$scope.multiselection = [];
    this.$scope.deactivateUsersIds = [];
    this.$scope.activateUsersIds = [];
  };

  private cancelBulkSelect = () => {
    this.$scope.roleMultiselection = [];
    this.$scope.multiselection = [];
    this.$scope.deactivateUsersIds = [];
    this.$scope.activateUsersIds = [];
    this.$scope.multiSelect = false;
  };

  private toggleRoleSwitcher = () => {
    this.$scope.showRolesSwitcher = !this.$scope.showRolesSwitcher;
    this.$scope.roleMultiselection = [];
  };

  private toggleMaximumLimitReached = () => {
    this.$scope.showMaximumLimitReached = !this.$scope.showMaximumLimitReached;
  };

  private usersChanged = () => {
    this.loadUsers();
  };

  private userChanged = (ev, data: { id: string; subscriptionType: string }) => {
    for (let i = 0; i < this.$scope.users.length; i++) {
      const user = this.$scope.users[i];

      if (user.id === data.id) {
        user.subscriptionType = data.subscriptionType;
        break;
      }
    }
  };

  private onPageChange = (page: number) => {
    this.$scope.currentPage = page;

    // avoiding triggering loadUsers during initial load because currentPage change
    if ((this.$scope.indicators.loading as IIndicatorProgress)?.progress === true) {
      return;
    }
    this.loadUsers();
  };

  private applyRoles = () => {
    this.$scope.indicators.applyingRoles = { progress: true };

    this.userService.updateRoles(this.$scope.multiselection, this.$scope.roleMultiselection).then(
      () => {
        delete this.$scope.indicators.applyingRoles;

        this.resetBulk();

        this.$rootScope.$broadcast("usersChanged", true);
      },
      (error) => (this.$scope.indicators.applyingRoles = { error })
    );
  };

  private resetBulk = () => {
    this.$scope.multiSelect = false;

    this.clearMultiselection();
    this.$scope.roleMultiselection = [];
    this.$scope.showRolesSwitcher = false;
    this.$scope.showActivateDialog = false;
    this.$scope.showMaximumLimitReached = false;
    this.$scope.showDeactivateDialog = false;
    this.$scope.showDeleteDialog = false;
  };

  private activateUsers = () => {
    this.$scope.indicators.activating = { progress: true };

    this.userService.activateUsers(this.$scope.multiselection).then(
      () => {
        delete this.$scope.indicators.activating;

        this.resetBulk();

        this.$rootScope.$broadcast("usersChanged", true);
      },
      (error) => (this.$scope.indicators.activating = { error })
    );
  };

  private deactivateUsers = () => {
    this.$scope.indicators.deactivating = { progress: true };

    this.userService.deactivateUsers(this.$scope.multiselection).then(
      () => {
        delete this.$scope.indicators.deactivating;

        this.resetBulk();

        this.$rootScope.$broadcast("usersChanged", true);
      },
      (error) => (this.$scope.indicators.deactivating = { error })
    );
  };

  private deleteUsers = () => {
    this.$scope.indicators.deleting = { progress: true };

    this.userService.deleteUsers(this.$scope.multiselection).then(
      () => {
        delete this.$scope.indicators.deleting;

        this.resetBulk();

        this.$rootScope.$broadcast("usersChanged", true);
      },
      (error) => (this.$scope.indicators.deleting = { error })
    );
  };

  private createUsersRequest = (): IRestLayerRequest => {
    const filtersOverride: Record<string, RestLayerFilterFactory> = {
      teamIds: generateDynamicTeamIdsFilter,
    };

    const request = toRestLayerRequest({ filters: this.$scope.filters, filtersOverride, sorting: this.$scope.sortingRules });

    request.fields = createRestLayerFields({
      picture: 1,
      email: 1,
      firstName: 1,
      lastName: 1,
      subscriptionType: 1,
      isActive: 1,
      roles: { id: 1, name: 1, team: { id: 1 } },
    });
    request.skip = (this.$scope.currentPage - 1) * usersPerPage;
    request.limit = usersPerPage;
    return request;
  };

  private clearMultiselection = (): void => {
    this.$scope.multiselection = [];
    this.$scope.deactivateUsersIds = [];
    this.$scope.activateUsersIds = [];
  };

  private loadUsers = (): void => {
    this.clearMultiselection();

    this.$scope.indicators.gettingUsers = { progress: true };

    const usersRequest = this.createUsersRequest();
    this.getUsers(usersRequest);
  };

  private getUsers = (request: IRestLayerRequest): void => {
    this.userService.getUsersV2<ICustomUser>(request).then(
      (users) => {
        delete this.$scope.indicators.gettingUsers;

        this.$scope.users = users.items;
        this.userMap = toIdMap(this.$scope.users);
        this.$scope.totalPages = Math.ceil(users.totalCount / usersPerPage);
      },
      (error) => (this.$scope.indicators.gettingUsers = { error })
    );
  };

  private toggleUserInMultiselection = (id: string) => {
    const index = this.$scope.multiselection.indexOf(id);
    if (index !== -1) {
      this.$scope.multiselection.splice(index, 1);
      if (this.userMap[id].isActive) {
        const deactivatedIndex = this.$scope.deactivateUsersIds.indexOf(id);
        this.$scope.deactivateUsersIds.splice(deactivatedIndex, 1);
      } else {
        const activatedIndex = this.$scope.activateUsersIds.indexOf(id);
        this.$scope.activateUsersIds.splice(activatedIndex, 1);
      }
    } else {
      this.$scope.multiselection.push(id);
      if (this.userMap[id].isActive) {
        this.$scope.deactivateUsersIds.push(id);
      } else {
        this.$scope.activateUsersIds.push(id);
      }
    }
  };
}
